/**
 * Map a rejected attach onto the form controls that caused it.
 *
 * `POST /merchant-employees` refuses an attach for specific, actionable
 * reasons — a phone auth-service will not accept, a role the merchant contour
 * may not assign, an identity already bound to another brand. The form used to
 * collapse every one of them into a single generic "something went wrong",
 * which is what made a failing "add employee" impossible to act on: the
 * merchant saw only that it did not work, never why.
 */

/** The create form's controls, in the vocabulary `setError` expects. */
export type CreateUserField = 'firstName' | 'lastName' | 'phone' | 'role';

/**
 * Wire field name → form control.
 *
 * Both the bare and the nested spelling are listed because the attach body
 * wraps its fields in `new_identity`, and the gateway has been observed to
 * report either depending on which layer rejected the request.
 */
const FIELD_BY_WIRE_NAME: Record<string, CreateUserField> = {
  phone: 'phone',
  'new_identity.phone': 'phone',
  role: 'role',
  'new_identity.role': 'role',
  first_name: 'firstName',
  'new_identity.first_name': 'firstName',
  last_name: 'lastName',
  'new_identity.last_name': 'lastName',
};

/**
 * Narrow the API's field errors to the ones this form can display.
 *
 * Returns only entries that map to a real control — an error naming a field
 * the form does not render would otherwise be set on nothing and vanish, so
 * the caller can check for an empty result and fall back to the form-level
 * slot instead.
 */
export function toCreateUserFieldErrors(
  fieldErrors: Record<string, string>,
): [CreateUserField, string][] {
  const matched: [CreateUserField, string][] = [];
  for (const [wireName, message] of Object.entries(fieldErrors)) {
    const field = FIELD_BY_WIRE_NAME[wireName];
    if (field) matched.push([field, message]);
  }
  return matched;
}
